import { Component, OnInit, OnDestroy, Input, OnChanges, SimpleChanges, Output, EventEmitter, forwardRef, ChangeDetectorRef, inject } from '@angular/core';
import { Subscription } from 'rxjs';
import { FiltersService } from '../filters.service';
import { KeyValuePair, KeyValueGroup } from '../../../models/models';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';

@Component({
  selector: 'app-drop-down-filter',
  templateUrl: './drop-down-filter.component.html',
  styleUrls: ['./drop-down-filter.component.scss'],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => DropDownFilterComponent),
      multi: true
    }
  ],
  standalone: false
})
export class DropDownFilterComponent implements OnInit, OnDestroy, OnChanges, ControlValueAccessor {
  private filterService = inject(FiltersService);
  private cd = inject(ChangeDetectorRef);

  @Input() placeholder: string; // used as a label
  @Input() emptyPlaceholder: string; // used as a placeholder
  @Input() name: string;
  @Input() items: KeyValuePair[];
  @Input() groupedItems: KeyValueGroup[]; // currently used only in autocomplete
  @Input() firstAsDefault = true;
  @Input() asyncItems = true;
  @Input() onlyCompare = false;
  @Input() showClearButton = false;
  @Input() autocompleteEmptyValue: string = null;
  @Input() isAutocomplete = false;
  @Input() width: string;
  @Input() multiSelect = false;
  @Input() disabled = false;
  @Input() tooltip: string;
  value: string;
  sub: Subscription;
  autocompleteValue: string;

  @Output() filterChanged = new EventEmitter<{ onlyComapre: boolean }>();

  // ControlValueAccessor implementation
  private onChange: (value: any) => void = () => { };
  private onTouched: () => void = () => { };

  // ControlValueAccessor methods
  writeValue(value: string): void {
    if (value !== undefined) {
      this.value = value;
      this.autocompleteValue = value;
      this.cd.detectChanges();
    }
  }

  registerOnChange(fn: any): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: any): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.disabled = isDisabled;
  }

  ngOnChanges(changes: SimpleChanges): void {
    const isItemsSet = (changes.items?.currentValue && (changes.items?.currentValue as any[]).length)
      || (changes.groupedItems?.currentValue && (changes.groupedItems?.currentValue as any[]).length);

    const hasNoValue = this.value === undefined || this.value === null;
    if (this.asyncItems && hasNoValue && isItemsSet) {
      if (this.firstAsDefault) {
        this.value = (changes.items.currentValue as KeyValuePair[])[0].id;
        this.filterService.setDefault(this.name, this.value);
        this.onChange(this.value);
      } else if (this.isAutocomplete && this.autocompleteEmptyValue) {
        this.autocompleteValue = this.autocompleteEmptyValue;
        this.filterService.setDefault(this.name, this.autocompleteValue);
        this.onChange(this.autocompleteValue);
      }
    }
  }

  ngOnInit(): void {
    this.sub = this.filterService.currentFilter.subscribe(p => {
      this.value = p[this.name];
      this.autocompleteValue = p[this.name];
      if (this.firstAsDefault) {
        if (p[this.name] === undefined && this.items && this.items.length) {
          this.value = this.items[0].id;
          this.autocompleteValue = this.items[0].id;
          this.onChange(this.value);
        }
      }
    });
  }
  ngOnDestroy(): void {
    this.sub?.unsubscribe();
  }

  valueChanged(value: string): void {
    this.value = value;
    this.onChange(value);
    this.onTouched();
    this.filterService.apply(this.name, value);
    this.filterChanged.emit({ onlyComapre: this.onlyCompare });
  }

  valueChangedMulti(value: string[]): void {
    const joinedValue = value.join(',');
    this.value = joinedValue;
    this.onChange(joinedValue);
    this.onTouched();
    this.filterService.apply(this.name, joinedValue);
    this.filterChanged.emit({ onlyComapre: this.onlyCompare });
  }

  clearValue(): void {
    this.value = undefined;
    this.onChange(undefined);
    this.onTouched();
    this.valueChanged(undefined);
  }
}
